import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ConfirmModal, useToast } from "../../../shared/components/SharedUI";
import { useLang } from "../../../shared/i18n/useLang";
import { overlayClose } from "../../../shared/utils/overlayClose";
import { usePermisos } from "../../../shared/utils/permisos";
import { crearGuarda, editarGuarda, listarGuardas, quitarGuarda } from "../services/seguridadService";
import fs from "../../finance/shared/fichas.module.css";
import s from "../seguridadAdmin.module.css";

function Campo({ label, req, ayuda, error, children, htmlFor }) {
  return (
    <div className={s.mCampo}>
      <label htmlFor={htmlFor}>
        {label}{req && <span className={s.req}> *</span>}
        {ayuda && <span className={s.ayuda}> - {ayuda}</span>}
      </label>
      {children}
      {error && <div className={s.mError}><i className="fa-solid fa-circle-exclamation" /> {error}</div>}
    </div>
  );
}

/** Crear o editar un guarda: datos, contraseña y si puede iniciar sesión. */
function GuardaModal({ guarda, onClose, onSaved }) {
  const L = useLang();
  const nuevo = !guarda;
  const [f, setF] = useState({
    first_name: guarda?.first_name || "", last_name: guarda?.last_name || "",
    username: guarda?.username || "", password: "", is_active: guarda ? guarda.is_active : true,
  });
  const [verClave, setVerClave] = useState(false);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);

  async function guardar() {
    setGuardando(true);
    setErrores({});
    try {
      const datos = nuevo ? f : {
        first_name: f.first_name, last_name: f.last_name, is_active: f.is_active,
        ...(f.password ? { password: f.password } : {}),
      };
      const r = nuevo ? await crearGuarda(datos) : await editarGuarda(guarda.id, datos);
      onSaved(r, nuevo);
    } catch (e) {
      setErrores(e.data && typeof e.data === "object" ? e.data : { detail: e.message });
    } finally {
      setGuardando(false);
    }
  }

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const iniciales = (`${f.first_name} ${f.last_name}`.trim() || f.username || "?")
    .split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

  return (
    <div className={s.overlay} {...overlayClose(onClose)}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={s.modalHead}>
          <div className={s.mTitulo}>
            <span className={s.mIcono}><i className={`fa-solid ${nuevo ? "fa-user-plus" : "fa-user-pen"}`} /></span>
            <div style={{ minWidth: 0 }}>
              <h3>{nuevo ? L("Nuevo guarda", "New guard") : L("Editar guarda", "Edit guard")}</h3>
              <p>{nuevo ? L("Va a entrar con este usuario a la app de Seguridad", "They will log in to the Security app with this user")
                        : `@${guarda.username}`}</p>
            </div>
          </div>
          <button type="button" className={s.iconBtn} onClick={onClose} aria-label={L("Cerrar", "Close")}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className={s.mCuerpo}>
          {errores.detail && (
            <div className={s.mAlerta}><i className="fa-solid fa-triangle-exclamation" /> {errores.detail}</div>
          )}

          {!nuevo && (
            <div className={s.mPerfil}>
              <span className={s.avatar}>{iniciales}</span>
              <div style={{ minWidth: 0 }}>
                <strong>{`${f.first_name} ${f.last_name}`.trim() || guarda.username}</strong>
                <span>{L("Oficial de seguridad", "Security officer")}</span>
              </div>
            </div>
          )}

          <div className={s.mGrid}>
            <Campo label={L("Nombre", "First name")} req error={errores.first_name} htmlFor="g-nombre">
              <input id="g-nombre" className={s.input} value={f.first_name} onChange={set("first_name")} autoFocus />
            </Campo>
            <Campo label={L("Apellidos", "Last name")} error={errores.last_name} htmlFor="g-apellidos">
              <input id="g-apellidos" className={s.input} value={f.last_name} onChange={set("last_name")} />
            </Campo>
          </div>

          <Campo label={L("Usuario", "Username")} req={nuevo} error={errores.username} htmlFor="g-usuario"
            ayuda={nuevo ? null : L("no se puede cambiar", "can't be changed")}>
            <div className={s.conIcono}>
              <i className="fa-solid fa-at" />
              <input id="g-usuario" className={s.input} value={f.username} onChange={set("username")}
                disabled={!nuevo} autoComplete="off" placeholder={L("Ej: seguridad.luis", "E.g.: security.luis")} />
            </div>
          </Campo>

          <Campo label={nuevo ? L("Contraseña", "Password") : L("Nueva contraseña", "New password")} req={nuevo}
            ayuda={nuevo ? L("mínimo 8 caracteres", "at least 8 characters") : L("dejala vacía para no cambiarla", "leave empty to keep it")}
            error={errores.password} htmlFor="g-clave">
            <div className={s.conIcono}>
              <i className="fa-solid fa-lock" />
              <input id="g-clave" className={s.input} type={verClave ? "text" : "password"} value={f.password}
                onChange={set("password")} autoComplete="new-password" style={{ paddingRight: 44 }} />
              <button type="button" className={s.verClave} onClick={() => setVerClave((v) => !v)}
                aria-label={verClave ? L("Ocultar contraseña", "Hide password") : L("Mostrar contraseña", "Show password")}>
                <i className={`fa-solid ${verClave ? "fa-eye-slash" : "fa-eye"}`} />
              </button>
            </div>
          </Campo>

          {!nuevo && (
            <label className={`${s.switch} ${s.mSwitch}`}>
              <input type="checkbox" checked={f.is_active} onChange={set("is_active")} />
              <span className={s.switchTrack} />
              <span>
                <strong>{f.is_active ? L("Activo", "Active") : L("Inactivo", "Inactive")}</strong>
                <small>{f.is_active ? L("Puede iniciar sesión en la app de Seguridad", "Can log in to the Security app")
                                    : L("No puede iniciar sesión hasta que lo actives", "Can't log in until you activate them")}</small>
              </span>
            </label>
          )}
        </div>

        <div className={s.mPie}>
          <button type="button" className={s.btn} onClick={onClose} disabled={guardando}>{L("Cancelar", "Cancel")}</button>
          <button type="button" className={`${s.btn} ${s.btnMarca}`} disabled={guardando} onClick={guardar}>
            <i className={`fa-solid ${guardando ? "fa-circle-notch fa-spin" : nuevo ? "fa-user-plus" : "fa-floppy-disk"}`} />
            {guardando ? L("Guardando…", "Saving…") : nuevo ? L("Crear guarda", "Create guard") : L("Guardar cambios", "Save changes")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Guardas del condominio: el administrador los crea (usuario y contraseña),
 * los edita, los desactiva o los quita. Cada guarda entra con su usuario desde
 * el celular o la tablet y cae directo en la app de Seguridad.
 */
export default function SeguridadGuardas() {
  const { showToast, toastEl } = useToast();
  const L = useLang();
  const puede = usePermisos();
  const [guardas, setGuardas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState(null);     // null | "nuevo" | guarda
  const [quitar, setQuitar] = useState(null);
  const [quitando, setQuitando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [estado, setEstado] = useState("");      // "" | "activos" | "inactivos"

  const cargar = useCallback(() => {
    setCargando(true);
    listarGuardas().then(setGuardas).catch((e) => showToast(e.message, false)).finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  async function confirmarQuitar() {
    setQuitando(true);
    try {
      await quitarGuarda(quitar.id);
      showToast(L(`${quitar.username} ya no tiene acceso a este condominio.`,
                  `${quitar.username} no longer has access to this condominium.`));
      setQuitar(null);
      cargar();
    } catch (e) {
      showToast(e.message, false);
    } finally {
      setQuitando(false);
    }
  }

  const nombre = (g) => `${g.first_name} ${g.last_name}`.trim();
  const iniciales = (g) => (nombre(g) || g.username).split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  const activos = guardas.filter((g) => g.is_active).length;
  const compartidos = guardas.filter((g) => g.otros_condominios > 0).length;

  const t = busqueda.trim().toLowerCase();
  const filtrados = guardas.filter((g) =>
    (!t || nombre(g).toLowerCase().includes(t) || g.username.toLowerCase().includes(t))
    && (!estado || (estado === "activos" ? g.is_active : !g.is_active)));

  return (
    <div className={s.page}>
      <span style={{ color: "var(--text-muted)" }}>
        <Link to="/" style={{ color: "inherit" }}>{L("Inicio", "Home")}</Link> /{" "}
        <strong style={{ color: "var(--text-primary)" }}>{L("Guardas", "Guards")}</strong>
      </span>
      <div className={s.header}>
        <div>
          <h2 className="mt-2 mb-1 p-0 title">{L("Guardas de seguridad", "Security guards")}</h2>
          <p>{L("Usuarios que usan la app de Seguridad en este condominio", "Users of the Security app in this condominium")}</p>
        </div>
        <div className={s.acciones}>
          {puede("seguridad_bitacora") && (
            <Link to="/seguridad/bitacora" className={s.btn}>
              <i className="fa-solid fa-clipboard-list" /> {L("Ver bitácora", "View log")}
            </Link>
          )}
          <button type="button" className={`${s.btn} ${s.btnMarca}`} onClick={() => setModal("nuevo")}>
            <i className="fa-solid fa-plus" /> {L("Nuevo guarda", "New guard")}
          </button>
        </div>
      </div>

      <div className={s.aviso}>
        <i className="fa-solid fa-circle-info" />
        <span>
          {L("El guarda inicia sesión con su usuario desde el celular o la tablet y entra directo a la app de Seguridad. No ve finanzas ni ningún otro módulo. Se puede instalar como app en el celular.",
             "The guard logs in with their username from a phone or tablet and goes straight to the Security app. They don't see finances or any other module. It can be installed as an app on the phone.")}
        </span>
      </div>

      {/* ── Resumen ───────────────────────────────────────────────────── */}
      <div className={s.stats}>
        <div className={`${s.stat} ${fs.orange}`}>
          <div className={s.statIcon}><i className="fa-solid fa-user-shield" /></div>
          <div>
            <div className={s.statLabel}>{L("Guardas", "Guards")}</div>
            <div className={s.statFila}><span className={s.statValor}>{cargando ? "-" : guardas.length}</span>
              <span className={s.statNota}>{L("en este condominio", "in this condominium")}</span></div>
          </div>
        </div>
        <div className={`${s.stat} ${fs.green}`}>
          <div className={s.statIcon}><i className="fa-solid fa-circle-check" /></div>
          <div>
            <div className={s.statLabel}>{L("Activos", "Active")}</div>
            <div className={s.statFila}><span className={s.statValor}>{cargando ? "-" : activos}</span>
              <span className={s.statNota}>{L("pueden iniciar sesión", "can log in")}</span></div>
          </div>
        </div>
        <div className={`${s.stat} ${fs.gray}`}>
          <div className={s.statIcon}><i className="fa-solid fa-user-slash" /></div>
          <div>
            <div className={s.statLabel}>{L("Inactivos", "Inactive")}</div>
            <div className={s.statFila}><span className={s.statValor}>{cargando ? "-" : guardas.length - activos}</span>
              <span className={s.statNota}>{L("sin acceso", "no access")}</span></div>
          </div>
        </div>
        <div className={`${s.stat} ${fs.blue}`}>
          <div className={s.statIcon}><i className="fa-solid fa-building" /></div>
          <div>
            <div className={s.statLabel}>{L("En otros condominios", "In other condominiums")}</div>
            <div className={s.statFila}><span className={s.statValor}>{cargando ? "-" : compartidos}</span>
              <span className={s.statNota}>{L("también trabajan en otro", "also work elsewhere")}</span></div>
          </div>
        </div>
      </div>

      {/* ── Filtros ───────────────────────────────────────────────────── */}
      <div className={s.filtros}>
        <div className={s.campo} style={{ flex: "1 1 280px" }}>
          <label htmlFor="gua-q">{L("Buscar", "Search")}</label>
          <div className={s.conIcono}>
            <i className="fa-solid fa-magnifying-glass" />
            <input id="gua-q" className={s.input} value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              autoComplete="off" placeholder={L("Nombre o usuario", "Name or username")} />
          </div>
        </div>
        <div className={s.campo} style={{ flex: "0 1 190px" }}>
          <label htmlFor="gua-estado">{L("Estado", "Status")}</label>
          <select id="gua-estado" className={s.input} value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">{L("Todos", "All")}</option>
            <option value="activos">{L("Activos", "Active")}</option>
            <option value="inactivos">{L("Inactivos", "Inactive")}</option>
          </select>
        </div>
      </div>

      {/* ── Tabla ─────────────────────────────────────────────────────── */}
      <div className={s.tabla}>
        <div className={s.scroll}>
          <table>
            <thead><tr>
              <th>{L("Guarda", "Guard")}</th>
              <th>{L("Usuario", "Username")}</th>
              <th>{L("Condominios", "Condominiums")}</th>
              <th>{L("Estado", "Status")}</th>
              <th style={{ textAlign: "right" }}>{L("Acciones", "Actions")}</th>
            </tr></thead>
            <tbody>
              {cargando ? (
                <tr><td colSpan={5} className={s.vacio}><i className="fa-solid fa-circle-notch fa-spin" /> {L("Cargando…", "Loading…")}</td></tr>
              ) : guardas.length === 0 ? (
                <tr><td colSpan={5} className={s.vacio}>{L("Todavía no hay guardas. Creá el primero.", "There are no guards yet. Create the first one.")}</td></tr>
              ) : filtrados.length === 0 ? (
                <tr><td colSpan={5} className={s.vacio}>{L("Ningún guarda coincide con la búsqueda.", "No guard matches the search.")}</td></tr>
              ) : filtrados.map((g) => (
                <tr key={g.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span className={s.avatar}>{iniciales(g)}</span>
                      <div className={s.persona}>
                        <strong>{nombre(g) || "-"}</strong>
                        <div className={s.personaSub}>{L("Oficial de seguridad", "Security officer")}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className={s.usuario}>@{g.username}</span></td>
                  <td>
                    {g.otros_condominios > 0
                      ? <span className={`${s.pastilla} ${fs.blue}`}><i className="fa-solid fa-building" />
                          {L(`Este y ${g.otros_condominios} más`, `This and ${g.otros_condominios} more`)}</span>
                      : <span>{L("Solo este", "Only this one")}</span>}
                  </td>
                  <td>
                    {g.is_active
                      ? <span className={`${s.pastilla} ${fs.green}`}><span className={s.punto} /> {L("Activo", "Active")}</span>
                      : <span className={`${s.pastilla} ${fs.red}`}><span className={s.punto} /> {L("Inactivo", "Inactive")}</span>}
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button type="button" className={s.iconBtn} disabled={!g.editable} style={{ marginRight: 6 }}
                      title={g.editable ? L("Editar", "Edit")
                        : L("Trabaja en otros condominios: lo edita el superadministrador",
                            "Works in other condominiums: the super administrator edits it")}
                      onClick={() => setModal(g)}>
                      <i className="fa-solid fa-pen" />
                    </button>
                    <button type="button" className={`${s.iconBtn} ${s.peligro}`}
                      title={L("Quitar de este condominio", "Remove from this condominium")} onClick={() => setQuitar(g)}>
                      <i className="fa-solid fa-user-minus" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!cargando && guardas.length > 0 && (
          <div className={s.pie}>
            <span>{L(`Mostrando ${filtrados.length} de ${guardas.length} guardas`, `Showing ${filtrados.length} of ${guardas.length} guards`)}</span>
          </div>
        )}
      </div>

      {modal && (
        <GuardaModal
          guarda={modal === "nuevo" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={(g, nuevo) => {
            setModal(null);
            showToast(nuevo ? L(`Guarda ${g.username} creado.`, `Guard ${g.username} created.`) : L("Cambios guardados.", "Changes saved."));
            cargar();
          }}
        />
      )}
      <ConfirmModal
        open={!!quitar}
        loading={quitando}
        message={quitar && L(
          `¿Quitar a ${quitar.username} de este condominio? Ya no va a poder usar la app de Seguridad aquí. Si no le queda ningún condominio, su usuario se desactiva (la bitácora conserva sus registros).`,
          `Remove ${quitar.username} from this condominium? They won't be able to use the Security app here. If they have no condominiums left, their user is deactivated (the log keeps their records).`)}
        onConfirm={confirmarQuitar}
        onCancel={() => setQuitar(null)}
      />
      {toastEl}
    </div>
  );
}
